import { useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";

interface InfoPopupProps {
  visible: boolean;
  onDismiss: () => void;
  worldIndex: number;
}

const WORLD_DESCRIPTIONS: Array<{
  body: React.ReactNode;
}> = [
  {
    body: (
      <>
        <p className="text-sm text-foreground leading-relaxed">
          This is a Gaussian splat generated with{" "}
          <span className="font-semibold">AnySplat</span>, on a{" "}
          <a
            href="https://www.google.com/maps/@40.7281,-73.9865,3a,75y,119h,90t"
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-blue-400 hover:text-blue-300"
          >
            Brooklyn street
          </a>
          . This was my PoC using Mapillary.
        </p>
      </>
    ),
  },
  {
    body: (
      <p className="text-sm text-foreground leading-relaxed">
        What if I took images from multiple locations, and created a splat with
        AnySplat? The splat is wider, but less detailed.
      </p>
    ),
  },
  {
    body: (
      <p className="text-sm text-foreground leading-relaxed">
        This splat combines 3 panoramas from different positions along the
        street. More coverage, but AnySplat trades off detail when stitching
        multiple viewpoints together.
      </p>
    ),
  },
];

export function InfoPopup({ visible, onDismiss, worldIndex }: InfoPopupProps) {
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.code === "KeyX") onDismiss();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [visible, onDismiss]);

  if (!visible) return null;

  const desc = WORLD_DESCRIPTIONS[worldIndex] ?? WORLD_DESCRIPTIONS[0];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
      <Card className="pointer-events-auto bg-card/80 backdrop-blur-md border-muted max-w-lg mx-4 shadow-2xl">
        <CardContent className="p-6 space-y-4">
          {desc?.body}
          <p className="text-xs text-muted-foreground text-center mt-4">
            Press{" "}
            <kbd className="px-1.5 py-0.5 rounded bg-muted text-foreground text-xs font-mono">
              X
            </kbd>{" "}
            to close
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
